import { getFunctions, httpsCallable } from 'firebase/functions'
import app from './firebase'
import { fetchEmployeeTimeByDayInRange } from './pwfEmployeeTimeService'
import { fetchProfitabilitySnapshotFromFirestore } from './pwfProfitabilitySnapshotService'
import type { TeamMember } from './orgChartService'
import type { PwfEmployeeTimeSpent } from '../types'
import { normalizePersonName } from '../utils/weekHours'

export type PwfStaffContact = {
  id: string
  name: string
  email: string | null
}

type StaffContactsResponse = {
  success: boolean
  contacts: PwfStaffContact[]
}

function emptyWeek(): number[] {
  return [0, 0, 0, 0, 0, 0, 0]
}

function cloneWeek(hours: number[]): number[] {
  return hours.map((h) => Number(h) || 0)
}

function applyDayRows(
  byContactId: Map<number, number[]>,
  byName: Map<string, number[]>,
  dayIndex: number,
  rows: PwfEmployeeTimeSpent[]
) {
  for (const row of rows) {
    const hours = Number(row.recordedHours) || 0
    if (hours <= 0) continue

    const contactId = Number(row.contactId)
    if (Number.isFinite(contactId)) {
      if (!byContactId.has(contactId)) byContactId.set(contactId, emptyWeek())
      byContactId.get(contactId)![dayIndex] = hours
    }

    if (row.name && row.name !== '—') {
      const key = normalizePersonName(row.name)
      if (!byName.has(key)) byName.set(key, emptyWeek())
      byName.get(key)![dayIndex] = hours
    }
  }
}

function countWeekHours(byContactId: Map<number, number[]>): number {
  let total = 0
  for (const week of byContactId.values()) {
    for (const h of week) total += h
  }
  return total
}

async function fetchStaffContacts(): Promise<PwfStaffContact[]> {
  const functions = getFunctions(app)
  const getPwfStaffContacts = httpsCallable<Record<string, never>, StaffContactsResponse>(
    functions,
    'getPwfStaffContacts',
    { timeout: 60_000 }
  )
  const result = await getPwfStaffContacts({})
  if (!result.data?.success || !Array.isArray(result.data.contacts)) {
    throw new Error('Unexpected PWF staff contacts response')
  }
  return result.data.contacts
}

/** Last-name match + first-name / prefix / initial overlap. */
function namesLooselyMatch(memberName: string, pwfName: string): boolean {
  const a = normalizePersonName(memberName)
  const b = normalizePersonName(pwfName)
  if (!a || !b) return false
  if (a === b) return true

  const aTokens = a.split(' ').filter((t) => t.length > 1)
  const bTokens = b.split(' ').filter((t) => t.length > 1)
  if (aTokens.length === 0 || bTokens.length === 0) return false

  const aLast = aTokens[aTokens.length - 1]
  const bLast = bTokens[bTokens.length - 1]
  if (aLast !== bLast) return false

  const aFirst = aTokens[0]
  const bFirst = bTokens[0]
  return (
    aFirst === bFirst ||
    aFirst.startsWith(bFirst) ||
    bFirst.startsWith(aFirst) ||
    aFirst[0] === bFirst[0]
  )
}

function matchMemberHours(
  member: TeamMember,
  byContactId: Map<number, number[]>,
  byName: Map<string, number[]>,
  emailToContactId: Map<string, number>
): number[] {
  const email = member.primaryEmail?.toLowerCase().trim()
  if (email) {
    const contactId = emailToContactId.get(email)
    if (contactId != null && byContactId.has(contactId)) {
      return cloneWeek(byContactId.get(contactId)!)
    }

    const local = normalizePersonName(email.split('@')[0].replace(/[._+]/g, ' '))
    if (local && byName.has(local)) return cloneWeek(byName.get(local)!)
  }

  const candidates = [
    normalizePersonName(member.name.fullName),
    normalizePersonName(`${member.name.givenName || ''} ${member.name.familyName || ''}`),
  ].filter(Boolean)

  for (const key of candidates) {
    if (byName.has(key)) return cloneWeek(byName.get(key)!)
  }

  for (const key of candidates) {
    const loose = [...byName.entries()].filter(([name]) => namesLooselyMatch(key, name))
    if (loose.length === 1) return cloneWeek(loose[0][1])
  }

  const family = normalizePersonName(member.name.familyName || '')
  if (family.length >= 2) {
    const matches = [...byName.entries()].filter(
      ([name]) => name === family || name.endsWith(` ${family}`)
    )
    if (matches.length === 1) return cloneWeek(matches[0][1])
  }

  return emptyWeek()
}

async function loadByDayBuckets(
  weekDayKeys: string[]
): Promise<Record<string, PwfEmployeeTimeSpent[]>> {
  const from = weekDayKeys[0]
  const to = weekDayKeys[weekDayKeys.length - 1]

  // Live first: correct date keys + up-to-date hours for the open week.
  try {
    const live = await fetchEmployeeTimeByDayInRange(from, to)
    if (Object.keys(live).length > 0) return live
  } catch (err) {
    console.warn('Live PWF by-day hours unavailable, trying snapshot:', err)
  }

  const fromSnap: Record<string, PwfEmployeeTimeSpent[]> = {}
  try {
    const snap = await fetchProfitabilitySnapshotFromFirestore()
    const byDay = snap.employeeTimeSpentByDay ?? {}
    for (const key of weekDayKeys) {
      if (byDay[key]?.length) fromSnap[key] = byDay[key]
    }
  } catch (err) {
    console.warn('Profitability snapshot unavailable for week hours:', err)
  }

  return fromSnap
}

/**
 * Load Mon–Sun PWF hours for Team Brevity members.
 * Tries live by-day callable first, then profitability snapshot.
 * Joins via email→contactId when available, with loose name fallback.
 */
export async function loadMemberWeekHours(
  members: TeamMember[],
  weekDayKeys: string[]
): Promise<Map<string, number[]>> {
  const byContactId = new Map<number, number[]>()
  const byName = new Map<string, number[]>()

  const byDay = await loadByDayBuckets(weekDayKeys)
  weekDayKeys.forEach((key, dayIndex) => {
    applyDayRows(byContactId, byName, dayIndex, byDay[key] ?? [])
  })

  if (countWeekHours(byContactId) === 0) {
    console.warn(
      'No PWF hours for week',
      weekDayKeys[0],
      '–',
      weekDayKeys[weekDayKeys.length - 1],
      '(live + snapshot). Boxes will stay grey until time exists or functions are deployed.'
    )
  }

  const emailToContactId = new Map<string, number>()
  try {
    const contacts = await fetchStaffContacts()
    for (const c of contacts) {
      const id = Number(c.id)
      if (!Number.isFinite(id)) continue
      if (c.email) emailToContactId.set(c.email.toLowerCase().trim(), id)
      if (c.name && c.name !== '—' && byContactId.has(id)) {
        const key = normalizePersonName(c.name)
        // Prefer contact-directory spelling as an alias onto the same week hours
        if (!byName.has(key)) byName.set(key, cloneWeek(byContactId.get(id)!))
      }
    }
  } catch (err) {
    console.warn('PWF staff contacts unavailable for email join:', err)
  }

  const byMemberId = new Map<string, number[]>()
  let matched = 0
  for (const member of members) {
    const week = matchMemberHours(member, byContactId, byName, emailToContactId)
    if (week.some((h) => h > 0)) matched += 1
    byMemberId.set(member.id, week)
  }

  if (byContactId.size > 0 && matched === 0) {
    console.warn(
      `PWF returned hours for ${byContactId.size} contacts but none matched Team Brevity members. Check emails / names.`
    )
  }

  return byMemberId
}
